import { StaticPageSeo } from '@app/components/core/seo/StaticPageSeo'
import { useTranslations } from '@i18n/useLanguage'
import { PageLayout } from '../layout/PageLayout'
import heroImage from '@app/assets/images/steward-ownership/hero-steward-ownership.png'
import whyStewardOwnedImage from '@app/assets/images/steward-ownership/waarom-steward-owned.png'
import howDoesItWorkImage from '@app/assets/images/steward-ownership/hoe-werkt-steward-ownership.png'
import whatDoesItMeanInPracticalImage from '@app/assets/images/steward-ownership/wat-betekent-het-in-de-praktijk.png'
import learnMoreAboutStewardOwnershipImage from '@app/assets/images/steward-ownership/meer-weten-over-steward-ownership.png'
import { Style } from 'inlines'
import { Hero } from '@app/components/blocks/hero/Hero'
import { ColumnsSection } from '@app/components/blocks/contentSection/ColumnsSection'
import useMediaQuery from '@app/util/useMediaQuery'
import { useMemo } from 'react'
import { OurStewards } from './blocks/OurStewards'
import { Title } from '@app/components/core/typography/Title'
import { OurStewardPartners } from './blocks/OurStewardPartners'
import { pages } from '@app/util/links'

export const StewardOwnership = () => {
  const t = useTranslations().app.stewardOwnershipPage
  const isMobile = useMediaQuery()

  const imageStyle: Style = useMemo(
    () => ({
      height: isMobile ? 'auto' : '31rem',
      width: isMobile ? '100%' : '31rem',
      aspectRatio: '1 / 1',
      objectFit: 'cover',
    }),
    [isMobile],
  )

  return (
    <>
      <PageLayout
        gap={isMobile ? '2.5rem' : '6.25rem'}
        marginTop="0"
      >
        <StaticPageSeo
          path={pages.stewardOwnership}
          pageTitle={t.title}
          description={t.description}
        />
        <Hero title={t.title} fullWidth backgroundSrc={heroImage} />

        <ColumnsSection
          style={{
            alignItems: 'center',
            flexDirection: 'row',
          }}
          columns={[
            {
              markdown: `## Waarom CrowdBuilding steward-owned is\n
De wooncrisis vraagt om langetermijnoplossingen. CrowdBuilding bouwt aan een toekomst waar duurzame woningen en buurten ontstaan op basis van samenwerking, gedeeld eigenaarschap en maatschappelijke waarde.\n
Daarom kiezen we ook als onderneming voor een radicaal andere vorm van eigendom: *steward-ownership*.\n
Zo voorkomen we dat commerciële belangen ooit belangrijker worden dan [de missie waarmee we zijn begonnen](${pages.aboutUs}): het mogelijk maken van collectief bouwen.`,
            },
            isMobile
              ? null
              : {
                  imageSrc: whyStewardOwnedImage,
                  imageAlt: 'waarom steward-owned',
                  imageStyle: {
                    ...imageStyle,
                    marginLeft: 'auto',
                  },
                },
          ]}
        />

        <ColumnsSection
          style={{
            alignItems: 'center',
            flexDirection: isMobile ? 'column' : 'row',
          }}
          columns={[
            {
              imageSrc: howDoesItWorkImage,
              imageAlt: 'hoe werkt steward-owned',
              imageStyle: {
                ...imageStyle,
                marginRight: 'auto',
              },
            },
            {
              markdown: `## Hoe werkt steward-ownership?\n
*CrowdBuilding is sinds 29 april 2025 officieel steward-owned.*\n
De missie wordt nog verder bewaakt door een onafhankelijke stichting: de [Golden Share Foundation](https://www.goldensharefoundation.org/). Deze stichting kan grote wijzigingen in de missie of eigendomsstructuur tegenhouden. Zo voorkomen we dat CrowdBuilding in de toekomst wordt overgenomen, of dat de onderneming van koers verandert op een manier die niet past bij het maatschappelijke doel.\n
Ook onze oprichters en investeerders verkiezen impact boven oneindige winst. Ze hebben vrijwillig een limiet gesteld aan de invloed die ze hebben en het rendement dat zij mogen ontvangen. Zodra investeringen zijn terugverdiend, gaan toekomstige winsten naar het bevorderen van de missie en maatschappelijke impact.\n
_💡CrowdBuilding is een van de eerste bedrijven in Nederland die met een steward-owned structuur groeikapitaal heeft opgehaald._
`,
            },
          ]}
        />

        <ColumnsSection
          style={{
            alignItems: 'center',
            flexDirection: isMobile ? 'column-reverse' : 'row',
          }}
          columns={[
            {
              markdown: `## Wat betekent het in de praktijk?\n
**CrowdBuilding is sinds 29 april 2025 officieel steward-owned.**\n
Dit betekent:
- Onze missie staat centraal en is juridisch vastgelegd.
- Zeggenschap ligt bij mensen die actief betrokken zijn bij het werk en het doel van de organisatie.
- Winst is een middel om maatschappelijke impact te bereiken, geen doel op zich. Investeerders krijgen een eerlijk, maar begrensd rendement.
- Gemeenten en partners kunnen bouwen op een stabiele, missiegedreven partner.
- Wooninitiatieven weten dat we structureel aan hun kant staan.\n
Ons doel is om CrowdBuilding in 2035 volledig over te dragen aan Stichting CrowdBuilding, zodat het platform op de lange termijn autonoom, missiegedreven en in dienst van de samenleving blijft opereren.
`,
            },
            {
              imageSrc: whatDoesItMeanInPracticalImage,
              imageAlt: 'wat betekent het in de praktijk',
              imageStyle: {
                ...imageStyle,
                marginLeft: 'auto',
              },
            },
          ]}
        />

        <div>
          <Title.H2
            style={{
              marginBottom: '1.75rem',
              marginTop: 0,
            }}
          >
            Onze partners
          </Title.H2>
          <OurStewardPartners />
        </div>

        <div>
          <Title.H2
            style={{
              marginBottom: '1.75rem',
              marginTop: 0,
            }}
          >
            Onze stewards
          </Title.H2>
          <OurStewards />
        </div>

        <ColumnsSection
          style={{
            alignItems: 'center',
            flexDirection: isMobile ? 'column' : 'row',
          }}
          columns={[
            {
              imageSrc: learnMoreAboutStewardOwnershipImage,
              imageAlt: 'meer weten over steward-ownership',
              imageStyle: {
                ...imageStyle,
                marginRight: 'auto',
              },
            },
            {
              markdown: `## Meer weten over steward-ownership?\n
Van Odin tot BuurtzorgT, van Bosch tot Patagonia: CrowdBuilding maakt deel uit van een groeiende beweging van steward-owned bedrijven die zich inzetten voor maatschappelijke waarde, niet voor aandeelhouderswaarde. Steward-ownership maakt dit mogelijk, op een manier die duurzaam, juridisch verankerd en toekomstbestendig is.\n
Ook de stap naar steward-ownership zetten? Vind voorbeelden en templates van CrowdBuilding en andere bedrijven op [steward-ownership.nl](https://steward-ownership.nl/bedrijven)`,
            },
          ]}
        />
      </PageLayout>
    </>
  )
}
